import DashboardAddCardModal from '@/components/ReportDashboard/MainPage/DashboardAddCardModal'
import DashboardCardCanvas from '@/components/ReportDashboard/MainPage/DashboardCardCanvas'
import DashboardEditControls from '@/components/ReportDashboard/MainPage/DashboardEditControls'
import DashboardEditGrid from '@/components/ReportDashboard/MainPage/DashboardEditGrid'
import DashboardTabs from '@/components/ReportDashboard/MainPage/DashboardTabs'
import FactorAnalyticsTopNav from '@/components/ReportDashboard/MainPage/FactorAnalyticsTopNav'
import ErrorDialog from '@/components/Shared/ErrorDialog'
import { Toaster } from '@/components/Shared/Toast'
import { COMPANY_COLORS } from '@/constants/colors.constant'
import {
  AssetClassEnumLookup,
  AssetClassNameLookup,
  RADashboardPortfolioType,
} from '@/constants/ReportDashboard/asset-enums'
import { RADashboardCardId, RAReportDataSource } from '@/constants/ReportDashboard/cards-enums'
import { lookupCardSources, lookupTabSources } from '@/constants/ReportDashboard/cards-sources'
import { FADashboardType } from '@/constants/ReportDashboard/dashboards/dashboard-types'
import DefaultAttributionDashboardLayout from '@/constants/ReportDashboard/dashboards/defaultAttribution'
import DefaultDashboardEquity from '@/constants/ReportDashboard/dashboards/defaultEquity'
import DefaultDashboardFixedIncome from '@/constants/ReportDashboard/dashboards/defaultFixedIncome'
import DefaultDashboardMultiAsset from '@/constants/ReportDashboard/dashboards/defaultMultiAsset'
import { ReportItem, ReportTotalItem } from '@/constants/ReportDashboard/report-items'
import { RADashboardTabId } from '@/constants/ReportDashboard/tab-enums'
import FAAttributionDataContextProvider from '@/contexts/FAAttributionDataContext'
import RADashboardDataContextProvider from '@/contexts/RADashboardDataContext'
import RADashboardLayoutContextProvider from '@/contexts/RADashboardLayoutContext'
import RADashboardThemeContextProvider from '@/contexts/RADashboardThemeContext'
import { useSchemaVersionGate } from '@/hooks/useSchemaVersionGate'
import {
  RADashboardCardPrefs,
  RADashboardLayout,
  VaRInfo,
  VaRPeriod,
  cardPrefsContextKey,
} from '@/interfaces/raDashboard.interface'
import { Dashboard, dashboardService } from '@/services/report-dashboard/dashboard.service'
import raDashboardDataService, { ReportDataCache } from '@/services/report-dashboard/raDashboard.data.service'
import {
  DEFAULT_ATTRIBUTION_DASHBOARD_KEY,
  DEFAULT_DASHBOARD_KEY,
  getDashboardKey,
  getDashboardType,
  getDashboardDisplayName as getSharedDashboardDisplayName,
  isDashboardOwnedByCurrentUser,
  isSelectableDashboardKey,
} from '@/utils/dashboardKeyUtils'
import {
  convertDateToInteger,
  getBusinessDaysInRange,
  getPreviousBusinessDay,
  previousWeekday,
  snapToWeekday,
  splitReportCoverage,
} from '@/utils/dateUtils'
import { validateCardPrefs, validateRADashboardLayout } from '@/utils/ReportDashboardValidation'
import { ThemeProvider, createTheme as createMuiTheme } from '@mui/material'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useImmer } from 'use-immer'
import PortfolioConfigHeader from '../../components/ReportDashboard/MainPage/PortfolioConfigHeader'
import { createTheme } from '../../constants/risk-dashboard-colors'
import { ReportLibraryRange, raDashboardService } from '../../services/report-dashboard/raDashboard.main.service'
import { getUserPreference, putUserPreference } from '../../services/userPreference.service'
import { AdvancedSettingsDialog, riskModelOptions } from './AdvancedSettingsDialog'
import { ReportCheckDialog } from './ReportCheckDialog'
import ReportProgressManager, { ProgressPollTarget } from './ReportProgressManager'
import { ViewSettingsDialog } from './ViewSettingDialog'

const RuntimeEditorFlag = true;

// Pick a valid display date for a sorted range of available dates.
// Keeps the current date when it's in range and a weekday; otherwise falls back
// to the latest available date. This is what keeps a preset/restored date (e.g.
// previous business day) from rendering the picker in an invalid (red) state
// once the real date range loads.
const reconcileDisplayDate = (sortedDates: string[], current: string): string => {
  if (sortedDates.length === 0) return current
  const min = sortedDates[0].split('T')[0]
  const max = sortedDates[sortedDates.length - 1].split('T')[0]
  const day = dayjs(current).day()
  const isWeekend = day === 0 || day === 6
  if (!current || current < min || current > max || isWeekend) return max
  return current
}

// Reconciles displayDate and endDate together, not independently — reconciling each on its own
// can leave both endpoints equal, or even inverted (e.g. a portfolio switch clears displayDate
// but leaves endDate at its old value, which can now sort before the freshly-snapped
// displayDate). FAAttributionDataContext refuses to fetch unless begin < end, so either case
// silently blanks the dashboard. Takes both current values directly (a pure computation) rather
// than nesting one state updater inside another, which React doesn't guarantee runs only once.
const reconcileDateRange = (
  sortedDates: string[],
  currentDisplay: string,
  currentEnd: string,
): { display: string; end: string } => {
  const end = currentEnd ? reconcileDisplayDate(sortedDates, currentEnd) : currentEnd
  let display = reconcileDisplayDate(sortedDates, currentDisplay)
  if (end && display >= end) {
    display = previousWeekday(dayjs(end)).format('YYYY-MM-DD')
  }
  return { display, end }
}

// Business days a library range actually has data for. GFR rows report beginDate === endDate
// (or omit endDate entirely) for their one data day. Attribution (GFPA) rows span a real
// beginDate/endDate pair, and the backend's beginDate is exclusive there, so only the business
// days after it through endDate (inclusive) actually have data.
const datesWithDataForRange = (range: ReportLibraryRange): string[] =>
  range.endDate && range.endDate !== range.beginDate
    ? getBusinessDaysInRange(range.beginDate, range.endDate).filter((d) => d !== range.beginDate)
    : getBusinessDaysInRange(range.beginDate, range.beginDate)

// The specific library row whose data-bearing span (see datesWithDataForRange) contains date —
// not just a row whose own beginDate equals it. A date can be covered as another row's interior
// day, and checkReportExists needs that row's real begin/end to resolve a matching reportId.
const findCoveringRange = (date: string, ranges: ReportLibraryRange[]): ReportLibraryRange | undefined =>
  ranges.find((r) => datesWithDataForRange(r).includes(date))

const portItemSourceMap: Partial<Record<RAReportDataSource, ReportTotalItem>> = {
  [RAReportDataSource.PortItemDV01]: ReportItem.DV01,
  [RAReportDataSource.PortItemConvexity]: ReportItem.Convexity,
  [RAReportDataSource.PortItemEffectiveDuration]: ReportItem.EffectiveDuration,
  [RAReportDataSource.PortItemYieldToMaturity]: ReportItem.YieldToMaturity,
  [RAReportDataSource.PortItemSpreadDuration]: ReportItem.SpreadDuration,
  [RAReportDataSource.PortItemYearsToMaturity]: ReportItem.YearsToMaturity,
  [RAReportDataSource.PortItemYieldToWorst]: ReportItem.YieldToWorst,
  [RAReportDataSource.PortItemCouponRate]: ReportItem.CouponRate,
  [RAReportDataSource.PortItemDividendYield]: ReportItem.DividendYield,
  [RAReportDataSource.PortItemAverageLife]: ReportItem.AverageLife,
  [RAReportDataSource.PortItemBeta]: ReportItem.Beta,
  [RAReportDataSource.PortItemBookToPrice]: ReportItem.BookToPrice,
  [RAReportDataSource.PortItemBookValue]: ReportItem.BookValue,
  [RAReportDataSource.PortItemCashFlowYield]: ReportItem.CashFlowYield,
  [RAReportDataSource.PortItemCurrentYield]: ReportItem.CurrentYield,
  [RAReportDataSource.PortItemEPRatio]: ReportItem.EPRatio,
  [RAReportDataSource.PortItemEarningsPerShare]: ReportItem.EarningsPerShare,
  [RAReportDataSource.PortItemEffectiveD2]: ReportItem.EffectiveD2,
  [RAReportDataSource.PortItemEffectiveD3]: ReportItem.EffectiveD3,
  [RAReportDataSource.PortItemDurContributionPct]: ReportItem.DurContributionPct,
  [RAReportDataSource.PortItemDurContributionYrs]: ReportItem.DurContributionYrs,
  [RAReportDataSource.PortItemEffectiveSpreadDuration]: ReportItem.EffectiveSpreadDuration,
  [RAReportDataSource.PortItemSpreadDurContributionPct]: ReportItem.SpreadDurContributionPct,
  [RAReportDataSource.PortItemSpreadDurContributionYrs]: ReportItem.SpreadDurContributionYrs,
  [RAReportDataSource.PortItemGainLoss]: ReportItem.GainLoss,
  [RAReportDataSource.PortItemMacaulayDuration]: ReportItem.MacaulayDuration,
  [RAReportDataSource.PortItemMarketCap]: ReportItem.MarketCap,
  [RAReportDataSource.PortItemMarketCapLog]: ReportItem.MarketCapLog,
  [RAReportDataSource.PortItemMarketValue]: ReportItem.MarketValue,
  [RAReportDataSource.PortItemMarketValuePar]: ReportItem.MarketValuePar,
  [RAReportDataSource.PortItemModifiedDuration]: ReportItem.ModifiedDuration,
  [RAReportDataSource.PortItemMomentum]: ReportItem.Momentum,
  [RAReportDataSource.PortItemMoodyQuality]: ReportItem.MoodyQuality,
  [RAReportDataSource.PortItemNotionalValue]: ReportItem.NotionalValue,
  [RAReportDataSource.PortItemOAS]: ReportItem.OAS,
  [RAReportDataSource.PortItemPBRatio]: ReportItem.PBRatio,
  [RAReportDataSource.PortItemPERatio]: ReportItem.PERatio,
  [RAReportDataSource.PortItemPctFaceMarketValue]: ReportItem.PctFaceMarketValue,
  [RAReportDataSource.PortItemPctMarketValue]: ReportItem.PctMarketValue,
  [RAReportDataSource.PortItemPrice]: ReportItem.Price,
  [RAReportDataSource.PortItemSPQuality]: ReportItem.SPQuality,
  [RAReportDataSource.PortItemSecurityCount]: ReportItem.SecurityCount,
  [RAReportDataSource.PortItemTotalAccruedInterest]: ReportItem.BaseTotalAccruedInterest,
  [RAReportDataSource.PortItemTrailingEPS]: ReportItem.TrailingEPS,
  [RAReportDataSource.PortItemVolatility]: ReportItem.Volatility,
  [RAReportDataSource.PortItemYearsToWorst]: ReportItem.YearsToWorst,
  // End PortItemSourceMap
}

const DL_MODE_CONTEXT = 'reportDashboard.DLMode'
const TOP_INFO_CONTEXT = 'reportDashboard.topInfo'

interface DLModePreference {
  isDarkMode: boolean
}

interface TopInfoAdvancedSettings {
  riskModel: string
  covarianceMatrix: string
  varConfidence: string
  timePeriodNumber: string
  timePeriodUnit: string
}

interface TopInfoPreference {
  selectedPortfolio: string
  selectedBenchmark: string
  selectedDashboard: string
  advancedSettings: TopInfoAdvancedSettings
}

const DefaultDashboardLookup: Record<RADashboardPortfolioType, RADashboardLayout> = {
  [RADashboardPortfolioType.FixedIncome]: DefaultDashboardFixedIncome,
  [RADashboardPortfolioType.Equity]: DefaultDashboardEquity,
  [RADashboardPortfolioType.MultiAsset]: DefaultDashboardMultiAsset,
}

type AnalyzedSnapshot = {
  portfolioName: string
  benchmarkName: string
  date: string
  endDate: string
  riskModel: string
  varConfidence: string
  timePeriodNumber: string
  timePeriodUnit: string
  dashboardName: string
}

const RADashboardPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [selectedPortfolio, setSelectedPortfolio] = useState<string>('')
  const [selectedBenchmark, setSelectedBenchmark] = useState<string>('')
  const [selectedDashboard, setSelectedDashboard] = useState<string>(DEFAULT_DASHBOARD_KEY)
  const [analysisType, setAnalysisType] = useState<string>('risk')
  const [dashboardData, setDashboardData] = useState<any>(null)
  const [isDashboardEditing, setIsDashboardEditing] = useState<boolean>(false)
  const [isSavingDashboardEdit, setIsSavingDashboardEdit] = useState<boolean>(false)
  const [isAddCardModalOpen, setIsAddCardModalOpen] = useState<boolean>(false)
  const preEditDashboardLayoutRef = useRef<RADashboardLayout | null>(null)
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false)
  const [marketValueNumber, setMarketValueNumber] = useState<number | null>(null)
  const [baseCurrency, setBaseCurrency] = useState<string | null>(null)
  const [assetClass, setAssetClass] = useState<RADashboardPortfolioType | null>(null)
  const [loadingMarketValue, setLoadingMarketValue] = useState<boolean>(false)
  const [progressActive, setProgressActive] = useState(false)
  // Either a single resolved report (Risk, or an already-covered Attribution range) or, while
  // generating, one entry per missing Attribution day — each is its own daily report server-side,
  // there's no combined "range" report, so completion just needs every entry to resolve.
  const [progressTargets, setProgressTargets] = useState<ProgressPollTarget[]>([])
  const [progressStatus, setProgressStatus] = useState<'ready' | 'failed' | null>(null)
  const [loadingData, setLoadingData] = useState(false)
  const [displayDate, setDisplayDate] = useState<string>('')
  // End date for Attribution dashboards' date-range picker.
  const [endDate, setEndDate] = useState<string>('')
  // Mirrors displayDate/endDate, updated every render — lets reconcileDateRange read both
  // values synchronously (a pure computation, not nested state updaters) from inside an async
  // fetch callback without risking a stale read across the await.
  const displayDateRef = useRef(displayDate)
  displayDateRef.current = displayDate
  const endDateRef = useRef(endDate)
  endDateRef.current = endDate
  const [availableDates, setAvailableDates] = useState<string[]>([])
  // Begin+end pairs from the report library. Rows aren't one-per-day — a row's own endDate can
  // differ from its beginDate and span several business days — so this drives both the
  // "already run" coverage check/calendar highlight (reportLibraryDateSet, expanding every row's
  // full span) and, for Attribution, the exact begin+end pair checkReportExists needs to resolve
  // a specific already-generated report.
  const [reportLibraryRanges, setReportLibraryRanges] = useState<ReportLibraryRange[]>([])
  const [loadingDates, setLoadingDates] = useState<boolean>(false)
  const [viewSettingsOpen, setViewSettingsOpen] = useState<boolean>(false)
  const [advancedSettingOpen, setAdvancedSettingOpen] = useState<boolean>(false)
  const [portfolioConfigCollapsed, setPortfolioConfigCollapsed] = useState<boolean>(false)
  const [reportCheckDialogOpen, setReportCheckDialogOpen] = useState<boolean>(false)
  const [reportCheckMessage, setReportCheckMessage] = useState<string>('')
  const [dashboardErrorDialogOpen, setDashboardErrorDialogOpen] = useState<boolean>(false);
  // Days that would need a GFPA report generated if the user hits Run Report on the check
  // dialog — set only by runAttributionAnalysis's "missing but coverable" branch, via
  // openReportCheckDialog, so it can never go stale relative to whichever message is showing.
  const [attributionRunnableDates, setAttributionRunnableDates] = useState<string[] | null>(null)
  // Days in the selected Attribution range that already have a report — non-null/non-empty
  // whenever the range is only partially covered, offering "View Anyway" regardless of whether
  // the remaining gap is also generatable (attributionRunnableDates may be null alongside this).
  const [attributionViewAnywayDates, setAttributionViewAnywayDates] = useState<string[] | null>(null)
  const [reportCheckData, setReportCheckData] = useState<{
    reportId: number
    exists: boolean
    message: string
  } | null>(null)
  const [riskModel, setRiskModel] = useState<string>('')
  const [covMatrixName, setCovMatrixName] = useState<string>('GCV1415')
  const [varConfidence, setVarConfidence] = useState<string>('')
  const [timePeriodNumber, setTimePeriodNumber] = useState<string>('')
  const [timePeriodUnit, setTimePeriodUnit] = useState<string>('')
  const [analyzedSnapshot, setAnalyzedSnapshot] =
    useState<AnalyzedSnapshot | null>(null)
  const [cardPrefs, setCardPrefs] = useState<RADashboardCardPrefs | null>(null)
  const topInfoPrefsLoaded = useRef(false)
  const lastLoadedDashboard = useRef<string | null>(null)
  const previousRouteDashboardKeyRef = useRef<string | null>(null)
  const [authorized, setAuthorized] = useState<boolean | null>(null)
  const [canManage, setCanManage] = useState<boolean>(false)
  const [isAdminAuthorized, setIsAdminAuthorized] = useState<boolean>(false)
  const hasCheckedAuth = useRef(false)
  const schemaVersionGate = useSchemaVersionGate()

  // Authorization check — must succeed before loading the page
  useEffect(() => {
    if (hasCheckedAuth.current) return
    hasCheckedAuth.current = true
    dashboardService
      .canRunDashboard()
      .then((isAuthorized) => setAuthorized(isAuthorized))
      .catch(() => setAuthorized(false))
    dashboardService
      .canManageDashboard()
      .then((isAuthorized) => setCanManage(isAuthorized))
      .catch(() => setCanManage(false))
    dashboardService
      .canAdminManageDashboard()
      .then((isAuthorized) => setIsAdminAuthorized(isAuthorized))
      .catch(() => setIsAdminAuthorized(false))
  }, [])

  const loadCardPrefsForDashboard = useCallback(async (dashboardName: string) => {
    if (lastLoadedDashboard.current === dashboardName) return
    lastLoadedDashboard.current = dashboardName
    try {
      const loaded = await getUserPreference<unknown>(cardPrefsContextKey(dashboardName))
      const validated = validateCardPrefs(loaded)
      setCardPrefs(validated ?? { dashboardName })
    } catch {
      // getUserPreference handles load failures (and toasts) itself; this guards
      // against an unexpected validateCardPrefs error — fall back to defaults.
      setCardPrefs({ dashboardName })
    }
  }, [])
  const [analysisData, setAnalysisData] = useImmer<ReportDataCache | null>(null)
  const engineBusy = progressActive || loadingData
  const busyLabel =
    progressActive && progressTargets.some((t) => t.uuid != null) ? 'Generating Report...' : 'Loading data...'
  const [reportIncomplete, setReportIncomplete] = useState(false)

  //TODO: Proving default dashboards, circumventing providers.
  // These should likely be in their own component with the overall canvas
  const [dashboardLayout, setDashboardLayout] = React.useState(DefaultDashboardFixedIncome)
  const [currentTab, setCurrentTab] = React.useState('')
  const [dashboardDataSources, setDashboardDataSources] = React.useState<Set<RAReportDataSource>>(new Set());

  const varPeriod: VaRInfo | null = useMemo(() => {
    const rate = Number(varConfidence)
    if (isNaN(rate)) {
      return null
    }
    const length = Number(timePeriodNumber)
    if (isNaN(length)) {
      return null
    }
    let period: VaRPeriod | null = null
    if (timePeriodUnit == 'day') {
      period = VaRPeriod.Days
    } else if (timePeriodUnit == 'month') {
      period = VaRPeriod.Months
    } else if (timePeriodUnit == 'year') {
      period = VaRPeriod.Years
    }
    if (period == null) {
      return null
    }
    return {
      length,
      rate,
      period,
    }
  }, [varConfidence, timePeriodNumber, timePeriodUnit])

  // Store portfolio state when navigating away
  const storePortfolioState = () => {
    const portfolioState = {
      selectedPortfolio,
      selectedBenchmark,
      selectedDashboard,
      displayDate,
      analysisType,
      riskModel,
      covMatrixName,
      varConfidence,
      timePeriodNumber,
      timePeriodUnit,
    }
    localStorage.setItem('radashboard-portfolio-state', JSON.stringify(portfolioState))
  }

  // Restore portfolio state when component mounts
  const restorePortfolioState = () => {
    const storedState = localStorage.getItem('radashboard-portfolio-state')
    if (storedState) {
      try {
        const state = JSON.parse(storedState)
        setSelectedPortfolio(state.selectedPortfolio || '')
        setSelectedBenchmark(state.selectedBenchmark || '')
        setSelectedDashboard(state.selectedDashboard || DEFAULT_DASHBOARD_KEY)
        setAnalysisType(state.analysisType || 'risk')
        setRiskModel(state.riskModel || '')
        setCovMatrixName(state.covMatrixName || 'GCV1415')
        setVarConfidence(state.varConfidence || '')
        setTimePeriodNumber(state.timePeriodNumber || '')
        setTimePeriodUnit(state.timePeriodUnit || '')
        setDisplayDate(state.displayDate || '')
      } catch (error) {
        console.error('Error restoring portfolio state:', error)
      }
    }
  }

  // Clear stored state
  const clearPortfolioState = () => {
    localStorage.removeItem('radashboard-portfolio-state')
  }

  // Load dark mode preference from backend on mount
  useEffect(() => {
    getUserPreference<DLModePreference>(DL_MODE_CONTEXT).then((pref) => {
      if (pref?.isDarkMode !== undefined) {
        setIsDarkMode(pref.isDarkMode)
      }
    })
  }, [])

  // Initialize display date with previous business day
  useEffect(() => {
    setDisplayDate(getPreviousBusinessDay())
  }, [])

  // Restore portfolio state when component mounts (runs before other effects)
  useEffect(() => {
    restorePortfolioState()
  }, [])

  useEffect(() => {
    if (authorized !== true) return
    const initializeDashboard = async () => {
      const portfolioData = await raDashboardService.getPortfolioData()
      const { portfolios, fullPortfolioData } = portfolioData
      // Avoid Adding Duplicate Benchmarks
      let benchmarks: typeof portfolioData.benchmarks = []
      let addedBenchmarks: Record<string, boolean> = {}
      portfolioData.benchmarks.forEach((bm) => {
        if (bm.apiName in addedBenchmarks) return
        benchmarks.push(bm)
        addedBenchmarks[bm.apiName] = true
      })
      try {
        // Load dashboard data
        const dashboardList = await dashboardService.getDashboards('RADashboard')
        setDashboardData({
          dashboardList,
          portfolios,
          benchmarks,
          fullPortfolioData,
        })

        // Load and apply user preferences for top info
        try {
          const pref = await getUserPreference<TopInfoPreference>(TOP_INFO_CONTEXT)
          if (pref) {
            if (pref.selectedPortfolio && portfolios.some((p: { name: string }) => p.name === pref.selectedPortfolio)) {
              setSelectedPortfolio(pref.selectedPortfolio)
              fetchPortfolioDates(pref.selectedPortfolio)
            }
            if (pref.selectedBenchmark) {
              setSelectedBenchmark(pref.selectedBenchmark)
            }
            if (pref.advancedSettings) {
              const {
                riskModel: rm,
                covarianceMatrix,
                varConfidence: vc,
                timePeriodNumber: tpn,
                timePeriodUnit: tpu,
              } = pref.advancedSettings
              if (rm) setRiskModel(rm)
              if (covarianceMatrix) setCovMatrixName(covarianceMatrix)
              if (vc) setVarConfidence(vc)
              if (tpn) setTimePeriodNumber(tpn)
              if (tpu) setTimePeriodUnit(tpu)
            }
          }
          // Route param takes priority over the saved preference; fall back to Default Dashboard.
          const routeDashboardKey = searchParams.get('dashboardKey')
          const prefDashboardKey = pref?.selectedDashboard
          let dashboardName: string
          if (isSelectableDashboardKey(routeDashboardKey, dashboardList)) {
            dashboardName = routeDashboardKey
          } else if (isSelectableDashboardKey(prefDashboardKey, dashboardList)) {
            dashboardName = prefDashboardKey
          } else {
            dashboardName = DEFAULT_DASHBOARD_KEY
          }
          previousRouteDashboardKeyRef.current = routeDashboardKey
          setSelectedDashboard(dashboardName)
          await loadCardPrefsForDashboard(dashboardName)
        } catch {
          // Silently ignore — preference unavailable, stay with defaults
        } finally {
          topInfoPrefsLoaded.current = true
        }
      } catch (error) {
        setDashboardData({
          dashboardList: [],
          portfolios,
          benchmarks,
          fullPortfolioData,
        })
        setDashboardErrorDialogOpen(true);
        topInfoPrefsLoaded.current = true
      }
    }
    initializeDashboard()
  }, [authorized])

  // Re-select the dashboard when the dashboardKey route param changes after the initial
  // load (e.g. clicking a dashboard link in the top nav) — the effect above only reads
  // the param once, on mount, so client-side navigations need their own listener. Compares
  // against the last-seen route value (rather than selectedDashboard) so that navigating to
  // a dashboard whose key was cleared/absent is also picked up correctly.
  useEffect(() => {
    if (!topInfoPrefsLoaded.current) return
    const routeDashboardKey = searchParams.get('dashboardKey')
    if (routeDashboardKey === previousRouteDashboardKeyRef.current) return
    previousRouteDashboardKeyRef.current = routeDashboardKey
    const dashboardList = dashboardData?.dashboardList ?? []
    const nextDashboard = isSelectableDashboardKey(routeDashboardKey, dashboardList)
      ? routeDashboardKey
      : DEFAULT_DASHBOARD_KEY
    setSelectedDashboard(nextDashboard)
    setPortfolioConfigCollapsed(false)
  }, [searchParams, dashboardData])

  // Save top info user preferences when portfolio, benchmark, or dashboard changes
  useEffect(() => {
    if (!topInfoPrefsLoaded.current) return
    putUserPreference<TopInfoPreference>(TOP_INFO_CONTEXT, {
      selectedPortfolio,
      selectedBenchmark,
      selectedDashboard,
      advancedSettings: {
        riskModel,
        covarianceMatrix: covMatrixName,
        varConfidence,
        timePeriodNumber,
        timePeriodUnit,
      },
    })
  }, [selectedPortfolio, selectedBenchmark, selectedDashboard])

  // Reload card prefs when the selected dashboard changes (after initial load)
  useEffect(() => {
    if (!topInfoPrefsLoaded.current) return
    loadCardPrefsForDashboard(selectedDashboard)
  }, [selectedDashboard, loadCardPrefsForDashboard])


  const handleAnalysisChunkLoaded = () => {
    var data = raDashboardDataService.getReportData()
    setAnalysisData((cache) => {
      if (cache == null) {
        return { ...data }
      } else {
        Object.assign(cache, data)
      }
    })
  }
  const wrapChunkPromise = async (apiPromise: Promise<unknown>) => {
    return apiPromise.then(handleAnalysisChunkLoaded)
  }
  const fetchDataBySources = (
    dataSources: Set<RAReportDataSource>,
    assetClass?: RADashboardPortfolioType | null,
  ) => {
    let apiPromises: Promise<unknown>[] = []
    let allPortReportItems = false
    let portfolioReportItems: ReportTotalItem[] = []
    dataSources.forEach((dataSrc) => {
      let dataPromise: Promise<unknown> | null = null
      switch (dataSrc) {
        case RAReportDataSource.RiskMetricsSummary:
          dataPromise = raDashboardDataService.getRiskMetricsSummary()
          break
        case RAReportDataSource.FactorLens:
          dataPromise = raDashboardDataService.getFactorLens(assetClass ? AssetClassNameLookup[assetClass] : undefined)
          break
        case RAReportDataSource.TopBottomSecPortfolio:
          dataPromise = raDashboardDataService.getPortfolioEndSecurities()
          break
        case RAReportDataSource.RiskOneYearHistory:
          dataPromise = raDashboardDataService.getOneYearTrailingRisk(
            new Date(displayDate.replaceAll("-", "/")),
          );
          break;
        case RAReportDataSource.HoldingsSecurityDetails:
          dataPromise = raDashboardDataService.getSecurityDetails()
          break
        case RAReportDataSource.PortTotalItems:
          allPortReportItems = true
          break
        default: {
          const reportItem = portItemSourceMap[dataSrc]
          if (reportItem !== undefined) portfolioReportItems.push(reportItem)
        }
      }
      if (dataPromise != null) {
        apiPromises.push(dataPromise.then(handleAnalysisChunkLoaded))
      }
    })
    if (allPortReportItems) {
      // Make API Call with every report item
      apiPromises.push(wrapChunkPromise(raDashboardDataService.getAllPortfolioReportItems()))
    } else if (portfolioReportItems.length) {
      // Make API call with only necessary items.
      apiPromises.push(wrapChunkPromise(raDashboardDataService.getPortfolioReportItems(...portfolioReportItems)))
    }
    return Promise.all(apiPromises)
  }
  const buildDataSources = (layout: RADashboardLayout = dashboardLayout): Set<RAReportDataSource> => {
    const dataSources: Set<RAReportDataSource> = new Set();
    Object.entries(layout.tabLayouts).forEach(([_tabId, tabLayout]) => {
      lookupTabSources(_tabId as RADashboardTabId).forEach(src => {
        dataSources.add(src);
      })
      tabLayout.cardsOrder.forEach((cardId) => {
        lookupCardSources(cardId as RADashboardCardId).forEach((src) => {
          dataSources.add(src);
        })
      })
    })
    return dataSources
  }

  const openReportCheckDialog = (
    message: string,
    runnableDates: string[] | null = null,
    viewAnywayDates: string[] | null = null,
  ) => {
    setReportCheckMessage(message)
    setAttributionRunnableDates(runnableDates)
    setAttributionViewAnywayDates(viewAnywayDates)
    setReportCheckDialogOpen(true)
  }

  const noGFPAReportMessage = (scope: 'full' | 'partial'): string =>
    `No attribution (GFPA) report exists for ${scope === 'full' ? 'the selected date range' : 'part of the selected date range'}.`

  const runRiskAnalysis = async () => {
    let reportCheckResult: { reportId: number; exists: boolean; message: string }
    try {
      reportCheckResult = await raDashboardService.checkReportExists(
        'GFR',
        selectedPortfolio,
        selectedBenchmark,
        displayDate,
        'POSTGRES',
      )
    } catch (error) {
      console.error('Error checking report existence:', error)
      openReportCheckDialog('Error checking report availability. Please try again.')
      return
    }

    setReportCheckData(reportCheckResult)

    if (reportCheckResult.exists) {
      setProgressTargets([{ uuid: null, reportId: reportCheckResult.reportId }])
      setProgressActive(true)
    } else {
      openReportCheckDialog(
        "Report doesn't exist for the selected parameters. Would you like to generate the report and continue, or change your selection?",
      )
    }
  }

  const runAttributionAnalysis = async () => {
    // A report only covers the selected range if every day the portfolio actually has data for
    // already has one — days with no portfolio data (market holidays) need no report and can't
    // block the range. Shared with the preset "already generated" badge (PortfolioConfigHeader)
    // so the two can't drift on what counts as covered.
    const { coverableDates, missingDates, coveredDates } = splitReportCoverage(
      displayDate,
      endDate || displayDate,
      availableDates,
      reportLibraryDateSet,
    )

    if (coverableDates.length === 0) {
      // Every day in the selected span is a holiday/weekend — there's no portfolio data
      // anywhere in range, so there's nothing to view or generate.
      openReportCheckDialog(`Portfolio data isn't available for the selected date range. Please select a different range.`)
      return
    }

    if (missingDates.length > 0) {
      // Days that already have a report — a non-empty set here means the user can still view
      // the range with whatever partial data already exists while the rest generates. Every
      // missing date is by construction a real portfolio-data day (coverableDates was already
      // filtered above), so it's always generatable.
      openReportCheckDialog(
        noGFPAReportMessage(coveredDates.length === 0 ? 'full' : 'partial'),
        missingDates,
        coveredDates.length > 0 ? coveredDates : null,
      )
      return
    }

    // GFPA needs both begin and end to resolve the reportId. checkReportExists matches an exact
    // already-generated report, not a stitched date range — library rows aren't one-per-day (a
    // row's own endDate can differ from its beginDate), so we look up the specific row whose
    // data actually covers the range's earliest real data day (displayDate itself may be a
    // holiday with no data — e.g. a preset beginning on Jan 1 — so it may not directly match any
    // row) and reuse its real begin/end, rather than the user's arbitrarily-selected endDate
    // which likely doesn't match any actual report.
    const attributionPair = findCoveringRange(coverableDates[0], reportLibraryRanges)
    if (!attributionPair) {
      // The coverage check above says every data day in range has a report, so this shouldn't
      // happen — no fallback, since resolving an unrelated/incomplete range would load a report
      // that doesn't match the selection.
      openReportCheckDialog('Error checking report availability. Please try again.')
      return
    }

    let reportCheckResult: { reportId: number; exists: boolean; message: string }
    try {
      reportCheckResult = await raDashboardService.checkReportExists(
        'GFPA',
        selectedPortfolio,
        selectedBenchmark,
        attributionPair.beginDate,
        'POSTGRES',
        attributionPair.endDate ?? undefined,
      )
    } catch (error) {
      console.error('Error checking report existence:', error)
      openReportCheckDialog('Error checking report availability. Please try again.')
      return
    }

    setReportCheckData(reportCheckResult)

    if (reportCheckResult.exists) {
      setProgressTargets([{ uuid: null, reportId: reportCheckResult.reportId }])
      setProgressActive(true)
    } else {
      openReportCheckDialog(noGFPAReportMessage('full'))
    }
  }

  const handleRunAnalysis = async () => {
    if (!selectedPortfolio || !selectedBenchmark || !displayDate) return

    setAnalysisData(null)
    setProgressStatus(null)
    setReportIncomplete(false)

    if (selectedDashboardType === FADashboardType.Attribution) {
      await runAttributionAnalysis()
    } else {
      await runRiskAnalysis()
    }
  }

  const fetchMarketValue = async (): Promise<RADashboardPortfolioType | null> => {
    if (!selectedPortfolio || !selectedBenchmark || !displayDate) return null

    setLoadingMarketValue(true)
    try {
      const marketValueData = await raDashboardService.getMarketValue(selectedPortfolio, selectedBenchmark, displayDate)
      setMarketValueNumber(marketValueData.marketValueNumber)
      setBaseCurrency(marketValueData.baseCurrency)
      const assetClassEnum = marketValueData.assetClass ? AssetClassEnumLookup[marketValueData.assetClass] : null
      setAssetClass(assetClassEnum ?? null)
      return assetClassEnum ?? null
    } catch (error) {
      console.error('Failed to fetch market value:', error)
      setMarketValueNumber(null)
      return null
    } finally {
      setLoadingMarketValue(false)
    }
  }

  const handleGenerateRiskReport = async () => {
    setReportCheckDialogOpen(false)
    try {
      const formattedDate = parseInt(displayDate.replace(/-/g, ''), 10)
      const runReportResponse = await raDashboardService.runReport(
        'GFR',
        selectedPortfolio,
        selectedBenchmark,
        'POSTGRES',
        formattedDate,
        'U',
        0,
        covMatrixName,
        0,
      )
      setProgressStatus(null)
      setProgressTargets([
        runReportResponse.reportId != null
          ? { uuid: null, reportId: runReportResponse.reportId }
          : { uuid: runReportResponse.requestUUID, reportId: null },
      ])
      setProgressActive(true)
    } catch (error) {
      console.error('Error generating report:', error)
      openReportCheckDialog('Error generating report. Please try again.')
    }
  }

  const handleGenerateAttributionReports = async () => {
    const dates = attributionRunnableDates
    setReportCheckDialogOpen(false)
    if (!dates || dates.length === 0) return

    // Fires every day's runReport at once (bounded by MAX_ATTRIBUTION_RUN_DAYS, since the Run
    // Report button is disabled past that). No chunking/concurrency-limiting for now — simplest
    // thing that works, revisit once we've seen how a full batch behaves against the real API.
    let targets: ProgressPollTarget[]
    try {
      const responses = await Promise.all(
        dates.map((date) =>
          raDashboardService.runAttributionReport(
            'GFPA',
            selectedPortfolio,
            selectedBenchmark,
            'POSTGRES',
            convertDateToInteger(previousWeekday(dayjs(date)).format('YYYY-MM-DD')),
            convertDateToInteger(date),
          ),
        ),
      )
      targets = responses.map((res) =>
        res.reportId != null ? { uuid: null, reportId: res.reportId } : { uuid: res.requestUUID, reportId: null },
      )
    } catch (error) {
      console.error('Error generating attribution reports:', error)
      // runReport's alreadyExists flag makes resubmitting already-generated days a no-op, so
      // retrying the same set is safe even if some of them actually did succeed.
      openReportCheckDialog('Error generating attribution reports. Click Run Report again to retry.', dates)
      return
    }

    setProgressStatus(null)
    setProgressTargets(targets)
    setProgressActive(true)
  }

  // "View Anyway" — proceed with whatever partial data already exists in the selected range
  // instead of waiting on the missing days. Any already-covered day's reportId works: the
  // dashboard's range queries key off displayDate/endDate (set generically by
  // handleProgressComplete), not the specific reportId, so this just needs one valid, real one.
  const handleViewAttributionAnyway = async () => {
    const coveredDates = attributionViewAnywayDates
    setReportCheckDialogOpen(false)
    if (!coveredDates || coveredDates.length === 0) return

    const coveredDate = coveredDates[0]
    const coveredPair = findCoveringRange(coveredDate, reportLibraryRanges)
    if (!coveredPair) {
      // coveredDate came straight from reportLibraryDateSet, which is built from exactly the
      // same ranges via findCoveringRange, so this shouldn't happen — keep the retry option
      // rather than dead-ending on "Change Selection" only.
      openReportCheckDialog('Could not find a report for the selected date. Please try again.', null, coveredDates)
      return
    }

    let reportCheckResult: { reportId: number; exists: boolean; message: string }
    try {
      reportCheckResult = await raDashboardService.checkReportExists(
        'GFPA',
        selectedPortfolio,
        selectedBenchmark,
        coveredPair.beginDate,
        'POSTGRES',
        coveredPair.endDate ?? undefined,
      )
    } catch (error) {
      console.error('Error checking report existence:', error)
      openReportCheckDialog('Error checking report availability. Please try again.', null, coveredDates)
      return
    }

    if (reportCheckResult.exists) {
      setReportCheckData(reportCheckResult)
      setProgressTargets([{ uuid: null, reportId: reportCheckResult.reportId }])
      setProgressActive(true)
    } else {
      // Distinct from the transport-error message above — the call succeeded, it just didn't
      // find what the coverage check expected to be there.
      openReportCheckDialog(
        'Could not resolve a report for the selected date, even though it appeared covered. Please try again.',
        null,
        coveredDates,
      )
    }
  }

  const handleGenerateReport = async () => {
    if (selectedDashboardType === FADashboardType.Attribution) {
      await handleGenerateAttributionReports()
    } else {
      await handleGenerateRiskReport()
    }
  }

  const handleProgressComplete = async (reportId: number) => {
    setProgressActive(false)
    setLoadingData(true)
    setReportIncomplete(false)
    try {
      raDashboardDataService.setReportId(reportId)
      const resolvedAssetClass = await fetchMarketValue()
      const layout =
        selectedDashboard === DEFAULT_DASHBOARD_KEY && resolvedAssetClass
          ? DefaultDashboardLookup[resolvedAssetClass]
          : dashboardLayout
      const dataSources = buildDataSources(layout)
      setDashboardDataSources(dataSources);
      await fetchDataBySources(dataSources, resolvedAssetClass)
      // Attribution data (totalReturn, daily summaries, factor contribution) flows through
      // FAAttributionDataContext, keyed off the resolved reportId + selected range.
      const loadedData = raDashboardDataService.getReportData()
      const sources = Array.from(dataSources)
      const requestedPortItems = sources
        .filter((src) => (src as string).startsWith('porttotalitem.'))
        .map((src) => (src as string).split('.')[1] as ReportTotalItem)
      const incompleteReasons: string[] = []
      if (loadedData == null) {
        incompleteReasons.push('loadedData is null')
      } else {
        sources.forEach((src) => {
          if (src === RAReportDataSource.PortTotalItems) {
            const nullItems = Object.entries(loadedData.portfolioReportItems)
              .filter(([, v]) => v === null)
              .map(([k]) => k)
            if (nullItems.length > 0) {
              incompleteReasons.push(`source "${src}" — portfolioReportItems null fields: ${nullItems.join(', ')}`)
            }
          } else if (!(src as string).startsWith('porttotalitem.')) {
            if ((loadedData as unknown as Record<string, unknown>)[src] === null) {
              incompleteReasons.push(`source "${src}" is null on loadedData`)
            }
          }
        })
        requestedPortItems.forEach((item) => {
          if (loadedData.portfolioReportItems[item] === null) {
            incompleteReasons.push(`requested porttotalitem "${item}" is null in portfolioReportItems`)
          }
        })
      }
      const incomplete = incompleteReasons.length > 0
      if (incomplete) {
        console.warn('[RADashboard] report flagged incomplete:', incompleteReasons)
      }
      // TODO: remove the false && when we believe we have all the items
      setReportIncomplete(false && incomplete)
      setReportCheckData((prev) => ({
        ...prev,
        reportId,
        exists: true,
        message: prev?.message ?? '',
      }))
      setProgressStatus('ready')
      // Analysis succeeded — collapse the config header to give the dashboard room.
      setPortfolioConfigCollapsed(true)
      setAnalyzedSnapshot({
        portfolioName: selectedPortfolioName,
        benchmarkName: selectedBenchmarkName,
        date: displayDate,
        endDate,
        riskModel,
        varConfidence,
        timePeriodNumber,
        timePeriodUnit,
        dashboardName: selectedDashboard,
      })
      // Refetch the report library dates so a date that was just generated gets
      // highlighted in the picker. Don't await, it manages its own loading
      // state and shouldn't delay the data load completing.
      void fetchReportLibraryDates()
    } catch {
      setReportIncomplete(true)
      setProgressStatus('failed')
    } finally {
      setLoadingData(false)
    }
  }

  // ReportProgressManager's single onComplete, fired once every current target has resolved to a
  // reportId, in the same order as the targets it was given (not completion order) — so
  // reportIds[0] is deterministic: the single report for Risk / an already-covered Attribution
  // range, or the earliest generated day for a batch (attributionRunnableDates preserves
  // chronological order). This reportId isn't inert — raDashboardDataService sends it on every
  // subsequent range query — so picking arbitrarily isn't safe; using the earliest day is a
  // reasonable default but hasn't been verified against a real multi-day generated batch yet.
  const handleProgressBatchComplete = (reportIds: number[]) => handleProgressComplete(reportIds[0])

  const handleProgressCancel = () => {
    setProgressActive(false)
  }

  const handleProgressFail = () => {
    setProgressActive(false)
    setProgressStatus('failed')
  }

  // Get data from dashboard state or use defaults
  const portfolios = dashboardData?.portfolios || []
  const benchmarks = dashboardData?.benchmarks || []

  const selectedPortfolioName = selectedPortfolio || ''
  const selectedBenchmarkName =
    benchmarks.find((b: { apiName: string; description: string }) => b.apiName === selectedBenchmark)?.description || ''
  const getDashboardDisplayName = (dashboardKey: string): string =>
    getSharedDashboardDisplayName(dashboardKey, dashboardData?.dashboardList ?? [])
  const selectedDashboardDisplayName = getDashboardDisplayName(selectedDashboard)

  const selectedDashboardType = useMemo(
    () => getDashboardType(selectedDashboard, dashboardData?.dashboardList ?? []),
    [selectedDashboard, dashboardData],
  )

  // Fetch report library dates when benchmark/portfolio or dashboard type changes — the reportType
  // (GFPA vs GFR) and whether begin+end ranges are needed both depend on selectedDashboardType.
  useEffect(() => {
    if (selectedPortfolio && selectedPortfolio !== 'Select Portfolio' && selectedBenchmark) {
      fetchReportLibraryDates()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBenchmark, selectedPortfolio, selectedDashboardType])

  const selectedDashboardOwned = useMemo(
    () => isDashboardOwnedByCurrentUser(selectedDashboard, dashboardData?.dashboardList ?? []),
    [selectedDashboard, dashboardData],
  )

  const selectedDashboardMeta = useMemo(
    () => (dashboardData?.dashboardList ?? []).find((d: Dashboard) => getDashboardKey(d) === selectedDashboard) ?? null,
    [selectedDashboard, dashboardData],
  )

  // Mirrors DashboardEditor's canEditMetadata gate (Editor/DashboardEditor.tsx) so the
  // runtime GUI editor honors the same lock/editable rules as the manage-dashboards editor.
  const canEditSelectedDashboard =
    selectedDashboardOwned &&
    !selectedDashboardMeta?.isLocked &&
    selectedDashboardMeta?.isEditable !== false

  const handleStartEditing = () => {
    if (!canEditSelectedDashboard) return
    preEditDashboardLayoutRef.current = dashboardLayout
    setIsDashboardEditing(true)
  }

  const handleCancelEditing = () => {
    if (preEditDashboardLayoutRef.current) {
      setDashboardLayout(preEditDashboardLayoutRef.current)
    }
    preEditDashboardLayoutRef.current = null
    setIsDashboardEditing(false)
  }

  // A card added during editing may need a source that wasn't requested by the original
  // analysis run. Refetch against the still-set reportId (not a fresh runReport) —
  // raDashboardDataService caches per source/reportId, so this only hits the network for
  // the newly-needed sources; everything already loaded is served from cache.
  const refetchNewDataSources = async (layout: RADashboardLayout) => {
    const dataSources = buildDataSources(layout)
    const hasNewSources = Array.from(dataSources).some((src) => !dashboardDataSources.has(src))
    if (!hasNewSources) return

    setLoadingData(true)
    try {
      await fetchDataBySources(dataSources, assetClass)
      setDashboardDataSources(dataSources)
    } catch (error) {
      console.error('Failed to fetch data for newly added card(s):', error)
      toast.error('Dashboard saved, but data for the new card failed to load. Please try again.')
    } finally {
      setLoadingData(false)
    }
  }

  const handleSaveEditing = async () => {
    if (!selectedDashboardMeta || !canEditSelectedDashboard) {
      toast.error('This dashboard can no longer be edited. Please try again later.')
      return
    }
    setIsSavingDashboardEdit(true)
    try {
      await dashboardService.putDashboard({ ...selectedDashboardMeta, jsonValue: dashboardLayout })
      toast.success('Dashboard saved')
      // Only clear the editing baseline/state on success — on failure, keep the
      // editor open with the pre-edit snapshot intact so the user can retry the
      // save or cancel back to the persisted layout.
      preEditDashboardLayoutRef.current = null
      setIsDashboardEditing(false)
    } catch (error) {
      console.error('Failed to save dashboard:', error)
      toast.error('Failed to save dashboard. Please try again later.')
      return
    } finally {
      setIsSavingDashboardEdit(false)
    }

    await refetchNewDataSources(dashboardLayout)
  }

  // If the selected dashboard changes while editing (route-param navigation, top nav,
  // etc.), the in-progress edit belongs to a dashboard that's no longer selected — drop
  // out of edit mode rather than let a stale pre-edit snapshot or a later save/cancel
  // apply the wrong dashboard's layout.
  useEffect(() => {
    setIsDashboardEditing(false)
    preEditDashboardLayoutRef.current = null
  }, [selectedDashboard])

  // Every path out of edit mode (cancel, successful save, or the dashboard-switch effect
  // above) should also close the add-card modal — otherwise its open state can outlive the
  // editing session and reappear unbidden the next time editing starts.
  useEffect(() => {
    if (!isDashboardEditing) setIsAddCardModalOpen(false)
  }, [isDashboardEditing])

  // Re-anchor the range whenever we enter an Attribution dashboard from something else (a Risk
  // dashboard, or first load) and there's no in-progress range from a prior Attribution visit to
  // preserve: default to the last month ending on whatever date was already selected, falling
  // back to today if none was selected. Dates are snapped off weekends immediately since
  // availableDates for the (possibly new) portfolio/benchmark hasn't loaded yet at this point;
  // fetchPortfolioDates/fetchReportLibraryDates reconcile both dates again once it does.
  const previousDashboardTypeRef = useRef<FADashboardType | null>(null)
  useEffect(() => {
    const enteringAttribution =
      selectedDashboardType === FADashboardType.Attribution &&
      previousDashboardTypeRef.current !== FADashboardType.Attribution
    previousDashboardTypeRef.current = selectedDashboardType

    if (selectedDashboardType !== FADashboardType.Attribution) return
    if (endDate && !enteringAttribution) return

    const anchor = snapToWeekday(displayDate ? dayjs(displayDate) : dayjs())
    setEndDate(anchor.format('YYYY-MM-DD'))
    setDisplayDate(snapToWeekday(anchor.subtract(1, 'month')).format('YYYY-MM-DD'))
    // displayDate is intentionally left out of the deps below: it's only read once, at the
    // moment of entering Attribution (guarded above). Including it would re-fire this effect
    // on every displayDate change while endDate is still empty, re-anchoring off its own prior
    // output each time and drifting the range backward on every render.
  }, [selectedDashboardType, endDate])

  const formatAnalysisDate = (isoDate: string): string => {
    if (!isoDate) return ''
    const [y, m, d] = isoDate.split('-')
    return y && m && d ? `${m}/${d}/${y}` : isoDate
  }

  const hasUnsavedChanges =
    analyzedSnapshot != null &&
    (analyzedSnapshot.portfolioName !== selectedPortfolioName ||
      analyzedSnapshot.benchmarkName !== selectedBenchmarkName ||
      analyzedSnapshot.date !== displayDate ||
      analyzedSnapshot.endDate !== endDate ||
      analyzedSnapshot.riskModel !== riskModel ||
      analyzedSnapshot.varConfidence !== varConfidence ||
      analyzedSnapshot.timePeriodNumber !== timePeriodNumber ||
      analyzedSnapshot.timePeriodUnit !== timePeriodUnit ||
      analyzedSnapshot.dashboardName !== selectedDashboard)

  const ANALYZED_COLOR = '#22c55e'
  const DRIFT_COLOR = COMPANY_COLORS.WARNING // cwan warning amber (#f0b018)

  // Dates (YYYY-MM-DD) that already have data (see datesWithDataForRange). Highlighted green in
  // PortfolioConfigHeader's calendar and used to decide which Attribution days still need a report.
  const reportLibraryDateSet = useMemo(() => {
    const dates = new Set<string>()
    reportLibraryRanges.forEach((range) => {
      datesWithDataForRange(range).forEach((d) => dates.add(d))
    })
    return dates
  }, [reportLibraryRanges])

  // Get related benchmark for selected portfolio
  const getRelatedBenchmark = () => {
    if (!selectedPortfolio || !dashboardData?.fullPortfolioData) return null
    const portfolio = dashboardData.fullPortfolioData.find((p: any) => p.name === selectedPortfolio)
    return portfolio?.benchmarkName || portfolio?.benchmarkDescription
  }

  const relatedBenchmarkName = getRelatedBenchmark()

  // Organize benchmarks: related first, then unrelated
  const organizedBenchmarks = () => {
    if (!relatedBenchmarkName) return benchmarks

    const related = benchmarks.find(
      (b: { apiName: string; description: string }) =>
        b.description === relatedBenchmarkName || b.apiName === relatedBenchmarkName,
    )
    const unrelated = benchmarks.filter(
      (b: { apiName: string; description: string }) =>
        b.description !== relatedBenchmarkName && b.apiName !== relatedBenchmarkName,
    )
    return related ? [related, ...unrelated] : unrelated
  }

  // Check if selected benchmark is related to portfolio
  const isRelatedBenchmark = () => {
    if (!selectedBenchmark || !relatedBenchmarkName) return false
    return selectedBenchmark === relatedBenchmarkName
  }

  // Handle portfolio selection and extract portfolio date
  const handlePortfolioChange = async (portfolioName: string) => {
    setSelectedPortfolio(portfolioName)
    setDisplayDate('') // Reset display date when portfolio changes
    setAvailableDates([]) // Reset available dates when portfolio changes
    setReportLibraryRanges([]) // Reset report library ranges

    // Set default values when user selects a portfolio
    if (portfolioName && portfolioName !== 'Select Portfolio') {
      setRiskModel('parametric')
      setVarConfidence('95')
      setTimePeriodNumber('1')
      setTimePeriodUnit('day')

      // Auto-select related benchmark
      const portfolio = dashboardData?.fullPortfolioData?.find((p: any) => p.name === portfolioName)
      const relatedBenchmark = portfolio?.benchmarkName || portfolio?.benchmarkDescription

      if (relatedBenchmark) {
        // Find the benchmark API name from the benchmarks list
        const relatedBenchmarkApi = benchmarks.find(
          (b: { apiName: string; description: string }) =>
            b.description === relatedBenchmark || b.apiName === relatedBenchmark,
        )?.apiName

        if (relatedBenchmarkApi) {
          setSelectedBenchmark(relatedBenchmarkApi)
        }
      }
    } else {
      // Reset to empty when no portfolio selected
      setRiskModel('')
      setVarConfidence('')
      setTimePeriodNumber('')
      setTimePeriodUnit('')
      setSelectedBenchmark('')
    }

    if (portfolioName && portfolioName !== 'Select Portfolio') {
      fetchPortfolioDates(portfolioName)
    }
  }

  // Fetch portfolio dates when portfolio is selected
  const fetchPortfolioDates = async (portfolioName?: string) => {
    const portfolio = portfolioName || selectedPortfolio
    if (!portfolio || portfolio === 'Select Portfolio') {
      return
    }

    setLoadingDates(true)
    try {
      const portfolioDatesResponse = await raDashboardService.getPortfolioDates(portfolio)
      const portfolioDates = portfolioDatesResponse.dates || []

      // Remove duplicates and sort
      const uniqueSortedDates = Array.from(new Set(portfolioDates.map((date) => date.split('T')[0])))
        .sort()
        .map((date) => date + 'T00:00:00')

      setAvailableDates(uniqueSortedDates)

      // Keep a valid display date/end date for the loaded range (snaps out-of-range endpoints
      // to the latest available one, jointly so they can't collapse onto — or invert past —
      // each other).
      const { display, end } = reconcileDateRange(uniqueSortedDates, displayDateRef.current, endDateRef.current)
      setDisplayDate(display)
      setEndDate(end)
    } catch (error) {
      console.error('Error fetching portfolio dates:', error)
      setAvailableDates([])
    } finally {
      setLoadingDates(false)
    }
  }

  // Fetch report library dates when benchmark is selected and merge with existing dates
  const fetchReportLibraryDates = async () => {
    if (!selectedPortfolio || selectedPortfolio === 'Select Portfolio' || !selectedBenchmark) {
      return
    }

    setLoadingDates(true)
    try {
      const reportLibraryDatesResponse = await raDashboardService.getReportLibraryDates(
        selectedDashboardType === FADashboardType.Attribution ? 'GFPA' : 'GFR',
        selectedPortfolio,
        selectedBenchmark,
        'POSTGRES',
      )

      const ranges = reportLibraryDatesResponse || []
      const isAttribution = selectedDashboardType === FADashboardType.Attribution
      // Attribution can only resolve a reportId from a real begin+end pair, so its picker/validation
      // must use the same filtered set Run Analysis does; GFR uses every row.
      const usableRanges = isAttribution ? ranges.filter((r) => r.endDate != null) : ranges
      // Kept for both dashboard types — reportLibraryDateSet below expands every row's
      // beginDate..endDate span, so the "already run" calendar highlight and Attribution's
      // coverage check both need the full rows, not just GFR's exact-match lookup.
      setReportLibraryRanges(usableRanges)

      // Begin/end dates drive the date picker / validation. Only the range's own endpoints are
      // known-good portfolio data days — interior business days (e.g. market holidays) aren't
      // guaranteed to have data just because they fall inside a report's span, so those are left
      // to reportLibraryDateSet (the "already run" axis) rather than availableDates.
      const beginEndDates = usableRanges.flatMap((r) => (r.endDate ? [r.beginDate, r.endDate] : [r.beginDate]))

      // Merge each range's dates with the existing available dates.
      const allDates = [...availableDates, ...beginEndDates]

      // Remove duplicates and sort
      const uniqueSortedDates = Array.from(new Set(allDates.map((date) => date.split('T')[0])))
        .sort()
        .map((date) => date + 'T00:00:00')

      setAvailableDates(uniqueSortedDates)

      // Keep a valid display date/end date for the loaded range (snaps out-of-range endpoints
      // to the latest available one, jointly so they can't collapse onto — or invert past —
      // each other).
      const { display, end } = reconcileDateRange(uniqueSortedDates, displayDateRef.current, endDateRef.current)
      setDisplayDate(display)
      setEndDate(end)
    } catch (error) {
      console.error('Error fetching report library dates:', error)
      // Don't clear available dates on error, just the report library
      setReportLibraryRanges([])
    } finally {
      setLoadingDates(false)
    }
  }

  const handleSaveSettings = (settings: {
    riskModel: string
    covarianceMatrix: string
    varConfidence: string
    timePeriodNumber: string
    timePeriodUnit: string
  }) => {
    setRiskModel(settings.riskModel)
    setCovMatrixName(settings.covarianceMatrix)
    setVarConfidence(settings.varConfidence)
    setTimePeriodNumber(settings.timePeriodNumber)
    setTimePeriodUnit(settings.timePeriodUnit)
    putUserPreference<TopInfoPreference>(TOP_INFO_CONTEXT, {
      selectedPortfolio,
      selectedBenchmark,
      selectedDashboard,
      advancedSettings: {
        riskModel: settings.riskModel,
        covarianceMatrix: settings.covarianceMatrix,
        varConfidence: settings.varConfidence,
        timePeriodNumber: settings.timePeriodNumber,
        timePeriodUnit: settings.timePeriodUnit,
      },
    })
  }

  const getRiskModelDisplayName = (value: string) => {
    const option = riskModelOptions.find((opt: { value: string; label: string }) => opt.value === value)
    return option?.label || value
  }

  const theme = createTheme(isDarkMode)
  const muiTheme = useMemo(
    () => createMuiTheme({
      typography: { fontFamily: theme.fontFamily }, 
      palette: { 
        mode: isDarkMode ? 'dark' : 'light',
        primary: {
          main: '#0a5eb2', //--cwan-palette-primary-main
          dark: '#0a5eb2', //--cwan-palette-primary-main
        },
        // cwan grey nav hover/selected (was MUI default white in dark mode)
        action: {
          hover: theme.buttonBackground,
        },
        text: {
          primary: theme.textPrimary,
        }
      } 
    }),
    [isDarkMode],
  )

  useEffect(() => {
    if (!dashboardData?.dashboardList) return

    const applyDashboard = (dashboard: RADashboardLayout) => {
      setDashboardLayout(dashboard)
      let initialTab = dashboard.initialTab
      const availableTabs = dashboard.tabsOrder
      if (availableTabs.length > 0 && !availableTabs.includes(initialTab)) {
        initialTab = availableTabs[0]
      }
      setCurrentTab(initialTab)
    }

    if (selectedDashboard === DEFAULT_DASHBOARD_KEY) {
      switch (assetClass) {
        case RADashboardPortfolioType.FixedIncome:
          applyDashboard(DefaultDashboardFixedIncome)
          break
        case RADashboardPortfolioType.Equity:
          applyDashboard(DefaultDashboardEquity)
          break
        case RADashboardPortfolioType.MultiAsset:
          applyDashboard(DefaultDashboardMultiAsset)
          break
        default:
          applyDashboard(DefaultDashboardEquity)
      }
      return
    }

    if (selectedDashboard === DEFAULT_ATTRIBUTION_DASHBOARD_KEY) {
      applyDashboard(DefaultAttributionDashboardLayout)
      return
    }

    const found = dashboardData.dashboardList.find(
      (d: Dashboard) => getDashboardKey(d) === selectedDashboard,
    )

    if (!found) {
      toast.error('Dashboard not found. Falling back to default')
      applyDashboard(DefaultDashboardFixedIncome)
      return
    }

    // The dashboard list only carries metadata (no jsonValue), so the
    // selected dashboard's full layout has to be fetched separately.
    let cancelled = false
    dashboardService
      .getDashboard(found.dashboardName, found.context, found.userId, found.currentUserOwned)
      .then((details) => {
        if (cancelled) return
        if (validateRADashboardLayout(details.jsonValue, details.dashboardType).valid) {
          applyDashboard(details.jsonValue as RADashboardLayout)
        } else {
          toast.error('Dashboard layout invalid. Falling back to default')
          applyDashboard(DefaultDashboardFixedIncome)
        }
      })
      .catch((error) => {
        if (cancelled) return
        console.error('Failed to load dashboard:', error)
        toast.error('Failed to load dashboard. Falling back to default')
        applyDashboard(DefaultDashboardFixedIncome)
      })

    return () => {
      cancelled = true
    }
  }, [selectedDashboard, dashboardData, assetClass])

  const selectTab = (tabKey: string) => {
    setCurrentTab(tabKey)
  }

  if (
    authorized === false ||
    (schemaVersionGate.loaded && !schemaVersionGate.isFactorAnalyticsEnabled)
  ) {
    return (
      <RADashboardThemeContextProvider isDarkMode={isDarkMode} theme={theme}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            backgroundColor: theme.background,
            color: theme.textPrimary,
            padding: '24px',
            textAlign: 'center',
          }}
        >
          There is a policy or version mismatch, this page is not viewable. Please contact support.
        </div>
      </RADashboardThemeContextProvider>
    )
  }

  if (authorized === null || !schemaVersionGate.loaded) {
    return (
      <RADashboardThemeContextProvider isDarkMode={isDarkMode} theme={theme}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            backgroundColor: theme.background,
            color: theme.textPrimary,
          }}
        />
      </RADashboardThemeContextProvider>
    )
  }

  return (
    <ThemeProvider theme={muiTheme}>
    <RADashboardThemeContextProvider isDarkMode={isDarkMode} theme={theme}>
      <RADashboardLayoutContextProvider layout={dashboardLayout} assetClass={assetClass}>
        <FAAttributionDataContextProvider
          reportId={reportCheckData?.reportId ?? null}
          portfolioName={analyzedSnapshot?.portfolioName ?? null}
          beginDate={analyzedSnapshot?.date ?? null}
          endDate={analyzedSnapshot?.endDate ?? null}
          dataSources={dashboardDataSources}
        >
        <RADashboardDataContextProvider
          marketValue={marketValueNumber}
          baseCurrency={baseCurrency}
          loading={loadingData}
          dataStore={analysisData}
          varInfo={varPeriod}
          covarianceMatrix={covMatrixName}
          riskModel={riskModel}
        >
          <div
            style={{
              padding: '0',
              margin: '0',
              minHeight: '100vh',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              background: theme.background,
              width: '100%',
            }}
          >
            <FactorAnalyticsTopNav
              canManage={canManage}
              canShowAttribution={schemaVersionGate.isAttributionEnabled}
              canShowRisk={schemaVersionGate.isRiskEnabled}
              dashboards={dashboardData?.dashboardList ?? []}
              isAdminAuthorized={isAdminAuthorized}
              isDarkMode={isDarkMode}
              selectedDashboardKey={selectedDashboard}
              onDarkModeToggle={(isDark) => {
                setIsDarkMode(isDark)
                putUserPreference<DLModePreference>(DL_MODE_CONTEXT, {
                  isDarkMode: isDark,
                })
              }}
            />

            {/* Selection Warning */}
              {hasUnsavedChanges && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '14px',
                    color: DRIFT_COLOR,
                    fontWeight: '600',
                    marginLeft: '40px',
                    marginBottom: '14px',
                  }}
                >
                  <span aria-hidden="true">⚠</span>
                  Selections changed — re-run analysis to update the report.
                </div>
              )}
            
            {/* Portfolio Configuration Card */}
            <PortfolioConfigHeader
              theme={theme}
              dashboardDisplayName={selectedDashboardDisplayName}
              onOpenAdvancedSettings={() => setAdvancedSettingOpen(true)}
              portfolios={portfolios}
              selectedPortfolio={selectedPortfolio}
              onPortfolioChange={handlePortfolioChange}
              benchmarks={organizedBenchmarks()}
              relatedBenchmarkName={relatedBenchmarkName}
              selectedBenchmark={selectedBenchmark}
              selectedBenchmarkName={selectedBenchmarkName}
              onBenchmarkChange={setSelectedBenchmark}
              dashboardType={selectedDashboardType}
              displayDate={displayDate}
              onDateChange={setDisplayDate}
              endDate={endDate}
              onEndDateChange={setEndDate}
              loadingDates={loadingDates}
              availableDates={availableDates}
              reportLibraryDateSet={reportLibraryDateSet}
              engineBusy={engineBusy}
              busyLabel={busyLabel}
              onRunAnalysis={handleRunAnalysis}
              collapsed={portfolioConfigCollapsed}
              onCollapsedChange={setPortfolioConfigCollapsed}
              loadingMarketValue={loadingMarketValue}
            />

            {/* Dashboard Content Area */}
            <div
              style={{
                margin: '0 40px',
                flex: 1,
                width: 'calc(100% - 80px)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                }}
              >
                {!progressActive && reportCheckData?.exists && (
                  <DashboardTabs selectedTab={currentTab} setSelectedTab={selectTab} />
                )}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginLeft: 'auto',
                  }}
                >
                  <ReportProgressManager
                    targets={progressTargets}
                    active={progressActive}
                    status={progressStatus}
                    isDarkMode={isDarkMode}
                    onComplete={handleProgressBatchComplete}
                    onCancel={handleProgressCancel}
                    onFail={handleProgressFail}
                  />
                  {!progressActive && reportCheckData?.exists && (
                    <DashboardEditControls
                      theme={theme}
                      editingEnabled={RuntimeEditorFlag}
                      isDashboardEditing={isDashboardEditing}
                      canEdit={canEditSelectedDashboard}
                      isSaving={isSavingDashboardEdit}
                      onStartEditing={handleStartEditing}
                      onCancelEditing={handleCancelEditing}
                      onSaveEditing={handleSaveEditing}
                      onAddCard={() => setIsAddCardModalOpen(true)}
                    />
                  )}
                </div>
              </div>
              {isDashboardEditing && (
                <DashboardAddCardModal
                  open={isAddCardModalOpen}
                  onClose={() => setIsAddCardModalOpen(false)}
                  layout={dashboardLayout}
                  setLayout={setDashboardLayout}
                  selectedTab={currentTab}
                  dashboardType={selectedDashboardType}
                />
              )}
              {!progressActive && !loadingData && !reportCheckData?.exists && progressStatus !== 'failed' && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 200,
                    color: theme.factorLensGridPrimaryColor,
                    fontSize: 14,
                    fontWeight: 500,
                    letterSpacing: '0.01em',
                  }}
                >
                  Please select a portfolio, benchmark, and date, then run the analysis to view results.
                </div>
              )}
              {!progressActive && reportCheckData?.exists && (
                isDashboardEditing ? (
                  <DashboardEditGrid
                    layout={dashboardLayout}
                    setLayout={setDashboardLayout}
                    selectedTab={currentTab}
                    dashboardType={selectedDashboardType}
                  />
                ) : (
                  <DashboardCardCanvas
                    layout={dashboardLayout}
                    selectedTab={currentTab}
                    dashboardType={selectedDashboardType}
                    cardPrefs={cardPrefs ?? undefined}
                    onCardPrefsChange={setCardPrefs}
                  />
                )
              )}
              <ViewSettingsDialog open={viewSettingsOpen} onOpenChange={setViewSettingsOpen} />
              <AdvancedSettingsDialog
                open={advancedSettingOpen}
                onOpenChange={setAdvancedSettingOpen}
                onSave={handleSaveSettings}
                initialSettings={{
                  riskModel,
                  covarianceMatrix: covMatrixName,
                  varConfidence,
                  timePeriodNumber,
                  timePeriodUnit,
                }}
              />
              <ReportCheckDialog
                open={reportCheckDialogOpen}
                onOpenChange={setReportCheckDialogOpen}
                message={reportCheckMessage}
                theme={theme}
                dashboardType={selectedDashboardType}
                attributionRunnableDates={attributionRunnableDates}
                attributionViewAnywayDates={attributionViewAnywayDates}
                onGenerateReport={handleGenerateReport}
                onViewAnyway={handleViewAttributionAnyway}
              />
              <ErrorDialog
                open={dashboardErrorDialogOpen}
                onOpenChange={setDashboardErrorDialogOpen}
                title='Dashboards Missing'
              >
                An error has occurred while attempting to load in your dashboards. The default dashboards are still available.<br />
                Refresh the page to try again. If this problem persists, please contact your service department.
              </ErrorDialog>
              {reportIncomplete && (
                <div
                  style={{
                    margin: '16px 0 8px 0',
                    padding: '12px 20px',
                    backgroundColor: '#fefce8',
                    border: '1px solid #fbbf24',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#92400e',
                  }}
                >
                  ⚠ Report is incomplete. Please inform the application team.
                </div>
              )}
            </div>
          </div>
          <Toaster />
        </RADashboardDataContextProvider>
        </FAAttributionDataContextProvider>
      </RADashboardLayoutContextProvider>
    </RADashboardThemeContextProvider>
    </ThemeProvider>
  )
}

export default RADashboardPage
